import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { upsertMedicalProfile } from "@/lib/healthOperations";

const EMPTY_FORM = {
  medicalConditions: "",
  emergencyPlan: "",
  physicianName: "",
  physicianPhone: "",
  insuranceProvider: "",
  insuranceMemberId: "",
  insuranceGroupNumber: "",
};

export default function MedicalProfileSheet({
  open,
  onOpenChange,
  child,
  profile,
  onSaved,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      medicalConditions: profile?.medical_conditions || "",
      emergencyPlan: profile?.emergency_plan || "",
      physicianName: profile?.physician_name || "",
      physicianPhone: profile?.physician_phone || "",
      insuranceProvider: profile?.insurance_provider || "",
      insuranceMemberId: profile?.insurance_member_id || "",
      insuranceGroupNumber: profile?.insurance_group_number || "",
    });
  }, [open, profile]);

  if (!child) return null;

  const field = (name) => (event) => setForm((current) => ({
    ...current,
    [name]: event.target.value,
  }));
  const onSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await upsertMedicalProfile({ childId: child.id, ...form });
      toast.success("Care details saved");
      onOpenChange(false);
      await onSaved?.();
    } catch (error) {
      toast.error(error.message || "Could not save care details");
    } finally {
      setSaving(false);
    }
  };
  const input = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="medical-profile-sheet">
        <SheetHeader className="text-left">
          <SheetTitle>{child.first_name}'s care details</SheetTitle>
          <SheetDescription>
            Keep care information current. Authorized center administrators can review it; assigned teachers only see the emergency plan when they open Safety.
          </SheetDescription>
        </SheetHeader>
        <form className="mt-5 space-y-4" onSubmit={onSubmit}>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Conditions or care considerations</span>
            <textarea data-testid="medical-profile-conditions" value={form.medicalConditions} onChange={field("medicalConditions")} rows={3} className={input} placeholder="Only include information your center should have." />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Emergency plan</span>
            <textarea data-testid="medical-profile-emergency-plan" value={form.emergencyPlan} onChange={field("emergencyPlan")} rows={4} className={input} placeholder="Action-time guidance for assigned classroom staff." />
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Physician</span>
              <input data-testid="medical-profile-physician" value={form.physicianName} onChange={field("physicianName")} className={input} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Physician phone</span>
              <input data-testid="medical-profile-physician-phone" type="tel" value={form.physicianPhone} onChange={field("physicianPhone")} className={input} />
            </label>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Insurance</p>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs text-neutral-600">Provider</span>
                <input data-testid="medical-profile-insurance-provider" value={form.insuranceProvider} onChange={field("insuranceProvider")} className={input} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-neutral-600">Member ID</span>
                <input data-testid="medical-profile-insurance-member" value={form.insuranceMemberId} onChange={field("insuranceMemberId")} className={input} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-neutral-600">Group number</span>
                <input data-testid="medical-profile-insurance-group" value={form.insuranceGroupNumber} onChange={field("insuranceGroupNumber")} className={input} />
              </label>
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="medical-profile-save" className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">
            {saving ? "Saving…" : "Save care details"}
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
